import React, { useState, useMemo, useRef, useEffect } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown, Filter } from "lucide-react";

/**
 * useTableState — owns sort + per-column filter state for a table.
 *
 * columns = [
 *   { key, label, value: (row) => sortable/filterable value,
 *     filterValues?: (row) => array of distinct values for filter dropdown,
 *     sortable?: bool, filterable?: bool, align?: 'left'|'center'|'right' }
 * ]
 *
 * Returns:
 *   rows               — filtered + sorted data
 *   headerPropsFor(k)  — props to spread on <SortFilterHeader>
 *   clearAll()         — reset sort + all filters
 *   activeFilterCount  — number of columns with active filters
 */
export function useTableState(data, columns, options = {}) {
  const [sort, setSort] = useState(options.initialSort || null);
  const [filters, setFilters] = useState({});

  const toggleSort = (key) => {
    setSort((cur) => {
      if (!cur || cur.key !== key) return { key, direction: "asc" };
      if (cur.direction === "asc") return { key, direction: "desc" };
      return null;
    });
  };

  const setColumnFilter = (key, nextSet) => {
    setFilters((cur) => {
      const next = { ...cur };
      if (!nextSet || nextSet.size === 0) {
        delete next[key];
      } else {
        next[key] = nextSet;
      }
      return next;
    });
  };

  const distinctByCol = useMemo(() => {
    const out = {};
    for (const col of columns) {
      if (!col.filterable) continue;
      const set = new Set();
      for (const row of data || []) {
        const vals = col.filterValues ? col.filterValues(row) : [col.value(row)];
        for (const v of vals || []) {
          if (v === null || v === undefined || v === "") continue;
          set.add(String(v));
        }
      }
      out[col.key] = Array.from(set).sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" })
      );
    }
    return out;
  }, [data, columns]);

  const rows = useMemo(() => {
    let result = data || [];

    for (const col of columns) {
      if (!col.filterable) continue;
      const sel = filters[col.key];
      if (!sel || sel.size === 0) continue;
      result = result.filter((row) => {
        const vals = col.filterValues ? col.filterValues(row) : [col.value(row)];
        if (!vals || vals.length === 0) return false;
        return vals.some((v) => v !== null && v !== undefined && sel.has(String(v)));
      });
    }

    if (sort) {
      const col = columns.find((c) => c.key === sort.key);
      if (col) {
        const dir = sort.direction === "asc" ? 1 : -1;
        result = [...result].sort((a, b) => {
          const av = col.value(a);
          const bv = col.value(b);
          const aEmpty = av === null || av === undefined || av === "";
          const bEmpty = bv === null || bv === undefined || bv === "";
          if (aEmpty && bEmpty) return 0;
          if (aEmpty) return 1;
          if (bEmpty) return -1;
          if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
          if (av instanceof Date && bv instanceof Date) return (av.getTime() - bv.getTime()) * dir;
          return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" }) * dir;
        });
      }
    }

    return result;
  }, [data, columns, filters, sort]);

  const headerPropsFor = (key) => {
    const col = columns.find((c) => c.key === key);
    if (!col) return { label: "", align: "left" };
    return {
      label: col.label,
      align: col.align || "left",
      sortable: !!col.sortable,
      filterable: !!col.filterable,
      sortDirection: sort && sort.key === key ? sort.direction : null,
      onSortToggle: col.sortable ? () => toggleSort(key) : undefined,
      filterOptions: col.filterable ? distinctByCol[key] || [] : [],
      selectedFilters: filters[key] || new Set(),
      onFiltersChange: col.filterable ? (next) => setColumnFilter(key, next) : undefined,
    };
  };

  const clearAll = () => {
    setSort(null);
    setFilters({});
  };

  const activeFilterCount = Object.values(filters).reduce(
    (n, s) => n + (s && s.size > 0 ? 1 : 0),
    0
  );

  return { rows, headerPropsFor, sort, filters, clearAll, activeFilterCount };
}

/**
 * <SortFilterHeader> — drop-in <th> with sort arrow + filter dropdown.
 * Spread props from useTableState's headerPropsFor(key).
 */
export function SortFilterHeader({
  label,
  align = "left",
  sortable = false,
  filterable = false,
  sortDirection = null,
  onSortToggle,
  filterOptions = [],
  selectedFilters = new Set(),
  onFiltersChange,
  className = "",
  thClassName,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const visibleOptions = useMemo(() => {
    if (!search) return filterOptions;
    const q = search.toLowerCase();
    return filterOptions.filter((v) => String(v).toLowerCase().includes(q));
  }, [filterOptions, search]);

  const toggleValue = (v) => {
    const next = new Set(selectedFilters);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onFiltersChange && onFiltersChange(next);
  };

  const selectAll = () => onFiltersChange && onFiltersChange(new Set(visibleOptions));
  const clearAll = () => onFiltersChange && onFiltersChange(new Set());

  const filterActive = selectedFilters.size > 0;
  const alignTh =
    align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";
  const alignRow =
    align === "center"
      ? "justify-center"
      : align === "right"
      ? "justify-end"
      : "justify-start";

  const SortIcon =
    sortDirection === "asc"
      ? ChevronUp
      : sortDirection === "desc"
      ? ChevronDown
      : ChevronsUpDown;

  const defaultTh = "px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider";
  const thClasses = thClassName !== undefined ? thClassName : defaultTh;

  return (
    <th className={`${thClasses} ${alignTh} ${className}`}>
      <div className={`flex items-center gap-1 ${alignRow}`}>
        <span
          className={sortable ? "cursor-pointer select-none hover:text-gray-700" : ""}
          onClick={sortable ? onSortToggle : undefined}
        >
          {label}
        </span>
        {sortable && (
          <button
            type="button"
            onClick={onSortToggle}
            className={`p-0.5 rounded ${
              sortDirection ? "text-cyan-700" : "text-gray-600 hover:text-gray-900"
            }`}
            aria-label="Sort"
          >
            <SortIcon className="w-4 h-4" strokeWidth={2.75} />
          </button>
        )}
        {filterable && (
          <div className="relative" ref={wrapperRef}>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className={`p-0.5 rounded ${
                filterActive
                  ? "text-cyan-700 bg-cyan-50"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              aria-label="Filter"
              title={filterActive ? `${selectedFilters.size} selected` : "Filter"}
            >
              <Filter className="w-4 h-4" strokeWidth={2.75} />
            </button>
            {open && (
              <div
                className={`absolute ${
                  align === "right" ? "right-0" : "left-0"
                } top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-56`}
              >
                <div className="p-2 border-b border-gray-100">
                  <input
                    type="text"
                    placeholder="Search values..."
                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs text-gray-700 normal-case font-normal tracking-normal focus:outline-none focus:ring-1 focus:ring-cyan-500"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="flex justify-between px-2 py-1 border-b border-gray-100 text-xs normal-case font-normal tracking-normal">
                  <button
                    type="button"
                    onClick={selectAll}
                    className="text-cyan-600 hover:underline"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={clearAll}
                    className="text-gray-500 hover:underline"
                  >
                    Clear
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {visibleOptions.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-400 italic normal-case font-normal tracking-normal">
                      No values
                    </div>
                  ) : (
                    visibleOptions.map((v) => (
                      <label
                        key={v}
                        className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer normal-case text-xs text-gray-700 font-normal tracking-normal"
                      >
                        <input
                          type="checkbox"
                          checked={selectedFilters.has(v)}
                          onChange={() => toggleValue(v)}
                          className="w-3.5 h-3.5 text-cyan-600 rounded border-gray-300 focus:ring-cyan-500"
                        />
                        <span className="truncate">{v}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </th>
  );
}
